import { Button } from '@civilgrid/ui/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { type CreateExtractTemplateBody, createExtractTemplate } from '~/api/extract';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import { MajorHeading } from '../common/Heading';
import { EXTRACT_ADMIN_STATE_QUERY_KEY } from './common';

type TFormValues = {
  workspace: string;
  name: string;
};

export default function CreateExtractTemplate() {
  const form = useForm<TFormValues>({
    defaultValues: { workspace: '', name: '' },
  });
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: CreateExtractTemplateBody) => createExtractTemplate(body),
    onSuccess: (res) => {
      Notify.info({ title: 'Create Extract Template', message: `Template "${res.template.name}" created.` });
      form.reset();
      queryClient.invalidateQueries({ queryKey: EXTRACT_ADMIN_STATE_QUERY_KEY });
    },
    onError: () => {
      Notify.error({
        title: 'Create Extract Template',
        message: 'An error occurred while creating the template. Check logs for details.',
      });
    },
  });

  const onSubmit: SubmitHandler<TFormValues> = (data) => {
    mutation.mutate({ workspace: data.workspace, name: data.name });
  };

  const isProcessing = mutation.isPending;

  return (
    <div className="flex flex-col items-start space-y-4">
      <MajorHeading tooltip="Creates a new FME extract template. Templates are then assigned to organizations to make them available on the extract download form.">
        Create Extract Template
      </MajorHeading>
      <Form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full max-w-md flex-col items-start space-y-4">
        <ControlledInput
          control={form.control}
          name="name"
          label="Name"
          isRequired
          placeholder="Ex: Caltrans Template"
          data-testid="extract-template-name-input"
        />
        <ControlledInput
          control={form.control}
          name="workspace"
          label="FME Workspace"
          isRequired
          placeholder="Ex: my-workspace.fmw"
          data-testid="extract-template-workspace-input"
        />
        <Button type="submit" loading={isProcessing} disabled={isProcessing} data-testid="extract-template-submit">
          Create Template
        </Button>
      </Form>
    </div>
  );
}
